import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  usePPEEquipment,
  useSafetyPackages,
  useSafetyProtocols,
  useSkills,
  useSlaRules,
} from "../settings/hooks";
import {
  getMasterActivityLogs,
  serviceProcedureApi,
  sopConfigApi,
  sopMaterialsApi,
  sopReadingsApi,
  sopTasksApi,
  sopTemplatesApi,
  utilityApi,
} from "./api";
import {
  CreateMasterSopRequest,
  SopDetailsParams,
  sopFilters,
  UpdateSopDocumentsRequest,
  UpdateSopMaterialsRequest,
  UpdateSopReadingsRequest,
  UpdateSopTasksRequest,
} from "./types";
import { transformSOPTemplate } from "./utils";

export const useServiceProcedureSummary = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.serviceProcedureSummary(),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return serviceProcedureApi.getServiceProcedureSummary(utilityId);
    }
  );
};

// Simplified SOP details hook - gets all data at once
export const useSopDetails = (
  params: Pick<SopDetailsParams, "id" | "remoteUtilityId">
) => {
  const { id, remoteUtilityId } = params;

  const queryKey = QueryKeyFactory.module.wx.master.sopMasterDetails(id);

  return useSmartQuery(
    queryKey,
    async () => {
      if (!id || !remoteUtilityId) {
        throw new Error("SOP ID or Remote Utility ID is missing");
      }
      return serviceProcedureApi.getSopDetails({
        id,
        remoteUtilityId,
        // No fieldName - gets all data
      });
    },
    {
      enabled: !!id && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// New hook for fetching specific tab data
export const useSopTabDetails = (params: {
  id: string;
  remoteUtilityId: string;
  fieldName: string;
}) => {
  const { id, remoteUtilityId, fieldName } = params;

  const queryKey = QueryKeyFactory.module.wx.master.sopTabDetails(fieldName);

  return useSmartQuery(
    queryKey,
    async () => {
      if (!id || !remoteUtilityId || !fieldName) {
        throw new Error("SOP ID, Remote Utility ID, or Field Name is missing");
      }
      return serviceProcedureApi.getSopTabDetails({
        id,
        remoteUtilityId,
        fieldName,
      });
    },
    {
      enabled: !!id && !!remoteUtilityId && !!fieldName,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// Hook for adding new tasks
export const useAddSopTask = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      taskData: {
        name: string;
        time: string;
        skill_id: number[];
        is_photo_req: string;
        downtime?: string;
      };
    }) => {
      return serviceProcedureApi.addSopTask({
        sopId: parseInt(params.sopId),
        taskData: params.taskData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("task"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for editing tasks
export const useEditSopTask = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      existingValue: string;
      taskData: {
        name: string;
        time: string;
        skill_id: number[];
        is_photo_req: string;
        downtime?: string;
      };
    }) => {
      return serviceProcedureApi.editSopTask({
        sopId: parseInt(params.sopId),
        existingValue: params.existingValue,
        taskData: params.taskData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("task"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for adding new materials
export const useAddSopMaterial = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      materialData: {
        name: string;
        size?: string;
        quantity_required: string;
        cost: string;
      };
    }) => {
      return serviceProcedureApi.addSopMaterial({
        sopId: parseInt(params.sopId),
        materialData: params.materialData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("material"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for editing materials
export const useEditSopMaterial = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      existingValue: string;
      materialData: {
        name: string;
        size?: string;
        quantity: string;
        cost: string;
      };
    }) => {
      return serviceProcedureApi.editSopMaterial({
        sopId: parseInt(params.sopId),
        existingValue: params.existingValue,
        materialData: params.materialData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("material"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for adding new readings
export const useAddSopReading = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      readingData: {
        name: string;
        unit: string;
        minimum?: string;
        maximum?: string;
        is_photo_req: string;
      };
    }) => {
      return serviceProcedureApi.addSopReading({
        sopId: parseInt(params.sopId),
        readingData: params.readingData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("readings"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for editing readings
export const useEditSopReading = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      existingValue: string;
      readingData: {
        name: string;
        unit: string;
        minimum?: string;
        maximum?: string;
        is_photo_req: string;
      };
    }) => {
      return serviceProcedureApi.editSopReading({
        sopId: parseInt(params.sopId),
        existingValue: params.existingValue,
        readingData: params.readingData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("readings"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for fetching SOP notes
export const useSopNotes = (sopId: string) => {
  const queryKey = QueryKeyFactory.module.wx.master.sopNotes(sopId);

  return useSmartQuery(
    queryKey,
    async () => {
      if (!sopId) {
        throw new Error("SOP ID is missing");
      }
      return serviceProcedureApi.getSopNotes(parseInt(sopId));
    },
    {
      enabled: !!sopId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// Hook for adding new notes
export const useAddSopNote = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      remoteUtilityId: string;
      noteData: {
        code: string;
        name: string;
        is_active: boolean;
        note_color: string;
        note_description: string;
      };
    }) => {
      return serviceProcedureApi.addSopNote({
        sopId: parseInt(params.sopId),
        remoteUtilityId: parseInt(params.remoteUtilityId),
        noteData: params.noteData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the notes data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopNotes(variables.sopId),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for editing notes
export const useEditSopNote = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: string;
      noteData: {
        code: string;
        name: string;
        is_active: boolean;
        note_color: string;
        note_description: string;
      };
    }) => {
      return serviceProcedureApi.editSopNote({
        sopId: parseInt(params.sopId),
        noteData: params.noteData,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the notes data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopNotes(variables.sopId),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

export const useSOPTemplates = (isDraft: boolean, namespace: string = "") => {
  const utilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = PAGE_SIZE;

  // Helper to get namespaced param
  const getParam = (key: string) =>
    searchParams.get(namespace ? `${namespace}_${key}` : key);
  // Helper to set namespaced param
  const setParam = (
    params: URLSearchParams,
    key: string,
    value: string | null
  ) => {
    const fullKey = namespace ? `${namespace}_${key}` : key;
    if (value && value.trim()) {
      params.set(fullKey, value);
    } else {
      params.delete(fullKey);
    }
  };

  // Parse filters from URL
  const getFiltersFromUrl = (): sopFilters => {
    const page = parseInt(getParam("page") || "1");

    const urlFilters: sopFilters = {
      page,
      limit: itemsPerPage,
      remote_utility_id: utilityId,
      is_draft: isDraft,
    };

    const statusParam = getParam("status");
    if (statusParam && statusParam !== "all") {
      urlFilters.status = statusParam;
    }

    const utilityTypeParam = getParam("utilityType");
    if (utilityTypeParam && utilityTypeParam !== "all") {
      urlFilters.utilityType = utilityTypeParam;
    }

    const priorityParam = getParam("priority");
    if (priorityParam && priorityParam !== "all") {
      urlFilters.priority = priorityParam;
    }

    const queryParam = getParam("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<sopFilters>(getFiltersFromUrl);

  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, utilityId]);

  const queryKeyFilters = useMemo(() => {
    const cleanFilters: Partial<sopFilters> = {
      page: filters.page,
      limit: filters.limit,
      is_draft: filters.is_draft,
    };

    // Only include defined filter values
    if (filters.search_data) cleanFilters.search_data = filters.search_data;
    if (filters.status) cleanFilters.status = filters.status;
    if (filters.utilityType) cleanFilters.utilityType = filters.utilityType;
    if (filters.priority) cleanFilters.priority = filters.priority;

    return cleanFilters;
  }, [filters]);

  const queryKey = QueryKeyFactory.module.wx.master.sopTemplates(
    isDraft,
    queryKeyFilters
  );

  const {
    data: apiResponse,
    isLoading,
    error,
    refetch,
  } = useSmartQuery(
    queryKey,
    async () => {
      if (!utilityId) throw new Error("Utility ID is required");
      return await sopTemplatesApi.getTemplates(utilityId, filters);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );

  const templates = useMemo(() => {
    if (!apiResponse?.results) return [];
    return apiResponse.results.map(transformSOPTemplate);
  }, [apiResponse]);

  const pagination = useMemo(() => {
    if (!apiResponse) return null;

    const totalItems = apiResponse.count;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const currentPage = filters.page;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [apiResponse, filters.page, itemsPerPage]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      setParam(params, "page", newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(
    (searchTerm: string, paramName: string = "query") => {
      const params = new URLSearchParams(searchParams);
      setParam(params, paramName, searchTerm);
      setParam(params, "page", "1"); // Reset to first page on search
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return {
    templates,
    isLoading,
    error,
    pagination,
    currentFilters: filters,
    handleSearch,
    handlePageChange,
    refetch,
    rawData: apiResponse,
  };
};

// Hook for toggling template status
export const useToggleTemplateStatus = () => {
  const queryClient = useQueryClient();
  const utilityId = useRemoteUtilityId();

  return useSmartMutation(
    async ({
      templateId,
      isActive,
    }: {
      templateId: string;
      isActive: boolean;
    }) => {
      if (!utilityId) throw new Error("Utility ID is required");

      return await sopTemplatesApi.toggleTemplateStatus(
        parseInt(templateId),
        utilityId,
        isActive
      );
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(true),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(false),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.serviceProcedureSummary(),
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Transform hooks for form-ready options
export const useServiceAssociationOptions = () => {
  const { data, isLoading, error, refetch } = useServiceAssociations();

  const options = useMemo(() => {
    if (!data?.result) return [];

    return data.result.map((item) => ({
      value: item.key.toString(),
      label: item.value,
    }));
  }, [data]);

  return { options, isLoading, error, refetch };
};

export const useServiceAssociations = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.serviceAssociations(utilityId),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return serviceProcedureApi.getServiceAssociations(utilityId);
    }
  );
};

export const useUtilityServices = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.utilityServices(utilityId),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return utilityApi.getUtilityServices(utilityId);
    }
  );
};

export const useUtilityServiceOptions = () => {
  const { data, isLoading, error } = useUtilityServices();

  const options = useMemo(() => {
    if (!data?.result) return [];

    // Filter only active services
    return data.result
      .filter((service) => service.isActive)
      .map((service) => ({
        value: service.name,
        label: service.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

// Hook for SOP Types
export const useSopTypes = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.sopTypes(utilityId),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return sopConfigApi.getSopTypes(utilityId);
    },
    {
      enabled: !!utilityId,
      staleTime: 15 * 60 * 1000, // 15 minutes - this data doesn't change often
    }
  );
};

// Hook for SOP Subtypes
export const useSopSubtypes = (typeCode: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.sopSubtypes(utilityId, typeCode),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      if (!typeCode) throw new Error("Type code is missing");
      return sopConfigApi.getSopSubtypes(utilityId, typeCode);
    },
    {
      enabled: !!utilityId && !!typeCode,
      staleTime: 15 * 60 * 1000, // 15 minutes - this data doesn't change often
    }
  );
};

// Transform hooks for form-ready options
export const useSopTypeOptions = () => {
  const { data, isLoading, error } = useSopTypes();

  const options = useMemo(() => {
    if (!data?.result) return [];

    return data.result
      .filter((item) => item.isActive)
      .map((item) => ({
        value: item.code,
        label: item.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

export const useSopSubtypeOptions = (typeCode: string) => {
  const { data, isLoading, error } = useSopSubtypes(typeCode);

  const options = useMemo(() => {
    if (!data?.result) return [];

    return data.result
      .filter((item) => item.isActive)
      .map((item) => ({
        value: item.code,
        label: item.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

export const useSLARulesOptions = () => {
  const { data, isLoading, error } = useSlaRules();

  const options = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => item.isActive)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

export const useSafetyPackageOptions = () => {
  const { data, isLoading, error } = useSafetyPackages();

  const options = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => item.isActive)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

export const useSafetyProtocolOptions = () => {
  const { data, isLoading, error } = useSafetyProtocols();

  const options = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => item.isActive)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

export const useSafetyEquipmentOptions = () => {
  const { data, isLoading, error } = usePPEEquipment();

  const options = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => item.isActive)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
  }, [data]);

  return { options, isLoading, error };
};

export const useSopTasks = (operationType: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.sopTasks(utilityId, operationType),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      if (!operationType) throw new Error("Operation type is missing");
      return sopTasksApi.getSopTasks(utilityId, operationType);
    },
    {
      enabled: !!utilityId && !!operationType,
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

export const useSopTaskOptions = (operationType: string) => {
  const { data, isLoading, error } = useSopTasks(operationType);

  const options = useMemo(() => {
    if (!data?.result) return [];

    return data.result
      .filter((task) => task.isActive)
      .map((task) => ({
        value: task.code,
        label: task.name,
        description: task.operationTypeDisplay,
        time: task.time,
        downtime: task.downtime,
        skillIds: task.skillId,
        photoRequired: task.isPhotoReq === "Yes",
        operationType: task.operationType,
      }));
  }, [data]);

  return { options, isLoading, error, rawData: data };
};

export const useSkillOptions = () => {
  const { data, isLoading, error } = useSkills();

  const options = useMemo(() => {
    if (!data) return [];

    return data
      .filter((skill) => skill.isActive)
      .map((skill) => ({
        value: skill.id, // Use ID as value
        label: skill.name, // Use skill name as label
        description: skill.description,
      }));
  }, [data]);

  // Helper function to get skill name by ID
  const getSkillNameById = useCallback(
    (skillId: number) => {
      if (!data) return "";
      const skill = data.find((s) => s.id === skillId);
      return skill?.name || "";
    },
    [data]
  );

  // Helper function to get skill ID by name
  const getSkillIdByName = useCallback(
    (skillName: string) => {
      if (!data) return null;
      const skill = data.find((s) => s.name === skillName);
      return skill?.id || null;
    },
    [data]
  );

  return {
    options,
    isLoading,
    error,
    rawData: data,
    getSkillNameById,
    getSkillIdByName,
  };
};

// Base hook for fetching materials
export const useSopMaterials = (operationType: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.sopMaterials(utilityId, operationType),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      if (!operationType) throw new Error("Operation type is missing");
      return sopMaterialsApi.getSopMaterials(utilityId, operationType);
    },
    {
      enabled: !!utilityId && !!operationType,
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

// Transformed hook for form-ready options
export const useSopMaterialOptions = (operationType: string) => {
  const { data, isLoading, error } = useSopMaterials(operationType);

  const options = useMemo(() => {
    if (!data?.result) return [];

    return data.result
      .filter((material) => material.isActive)
      .map((material) => ({
        value: material.code,
        label: material.name,
        description: material.operationTypeDisplay,
        size: material.size,
        quantity: material.quantity,
        cost: material.costPerUnit,
        operationType: material.operationType,
      }));
  }, [data]);

  return { options, isLoading, error, rawData: data };
};

export const useSopReadings = (operationType: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.sopReadings(utilityId, operationType),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      if (!operationType) throw new Error("Operation type is missing");
      return sopReadingsApi.getSopReadings(utilityId, operationType);
    }
  );
};

// Transformed hook for form-ready options
export const useSopReadingOptions = (operationType: string) => {
  const { data, isLoading, error } = useSopReadings(operationType);

  const options = useMemo(() => {
    if (!data?.result) return [];

    return data.result
      .filter((result) => result.isActive)
      .map((reading) => ({
        value: reading.key || reading.code,
        label: reading.name,
        description: reading.operationTypeDisplay,
        unit: reading.unit,
        minimum: reading.minimum,
        maximum: reading.maximum,
        photoRequired: reading.isPhotoReq === "Yes",
        operationType: reading.operationType,
      }));
  }, [data]);

  return { options, isLoading, error, rawData: data };
};
export const useCreateSop = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: CreateMasterSopRequest) => {
      return serviceProcedureApi.createSop(data);
    },
    {
      onSuccess: (response) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.serviceProcedureSummary(),
        });

        // Optionally invalidate templates list
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(true),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(false),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};
export const useUpdateSopById = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: number; data: CreateMasterSopRequest }) => {
      return serviceProcedureApi.updateSopById(params.sopId, params.data);
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate the specific SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });

        // Invalidate SOP templates list
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(true),
          exact: false,
        });

        // Invalidate service procedure summary
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.serviceProcedureSummary(),
        });

        // Invalidate edit details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopEditDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });

        toast({
          title: "Success",
          description: "SOP updated successfully",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update SOP",
          variant: "destructive",
        });
      },
    }
  );
};
export const useUpdateSopTasks = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: number; data: UpdateSopTasksRequest }) => {
      return serviceProcedureApi.updateSopTasks(params.sopId, params.data);
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for updating SOP with materials
export const useUpdateSopMaterials = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: number; data: UpdateSopMaterialsRequest }) => {
      return serviceProcedureApi.updateSopMaterials(params.sopId, params.data);
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Hook for updating SOP with readings
export const useUpdateSopReadings = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: number; data: UpdateSopReadingsRequest }) => {
      return serviceProcedureApi.updateSopReadings(params.sopId, params.data);
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

// Generic hook for updating any part of SOP
export const useUpdateSop = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: number; data: any }) => {
      return serviceProcedureApi.updateSop(params.sopId, params.data);
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });
      },
      onError: (error: any) => {},
    }
  );
};

export const useTaxRates = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.taxRates(utilityId),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return sopConfigApi.getTaxRates(utilityId);
    },
    {
      enabled: !!utilityId,
    }
  );
};
export const useUpdateSopDocuments = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: number; data: UpdateSopDocumentsRequest }) => {
      return serviceProcedureApi.updateSopDocuments(params.sopId, params.data);
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });
      },
    }
  );
};
export const useCreateMasterSop = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: {
      sopId: number;
      status: number;
      reviewPublishUpdated: boolean;
    }) => {
      return serviceProcedureApi.createMasterSOP(
        params.sopId,
        params.status,
        params.reviewPublishUpdated
      );
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId.toString()
          ),
          exact: false,
        });
      },
    }
  );
};
export const useSopDetailsForEdit = (sopId: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.master.sopEditDetails(sopId),
    async () => {
      if (!sopId) throw new Error("SOP ID is required for editing");
      return serviceProcedureApi.getSopDetails({
        id: sopId,
        remoteUtilityId: utilityId,
      });
    },
    {
      enabled: !!sopId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useGetMasterActivityLogs = (
  sopId: string,
  page: number = 1,
  limit: number = 10
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    // Include page and limit in query key for proper caching
    QueryKeyFactory.module.wx.master.activityLogs(sopId, page, limit),
    () => getMasterActivityLogs(remoteUtilityId, sopId, page, limit),
    {
      enabled: !!sopId && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};
export const useCloneSopTemplate = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (templateId: string) => {
      return await serviceProcedureApi.cloneSopTemplate(parseInt(templateId));
    },
    {
      onSuccess: (response, templateId) => {
        // Invalidate templates list to show the new cloned template
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(true), // Invalidate drafts
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTemplates(false), // Invalidate active templates
          exact: false,
        });

        // Invalidate summary data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.serviceProcedureSummary(),
        });
      },
      onError: (error: any) => {},
    }
  );
};
export const useDeleteSopTask = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: string; taskName: string }) => {
      return serviceProcedureApi.deleteSopTask({
        sopId: parseInt(params.sopId),
        taskName: params.taskName,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("task"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });

        toast({
          title: "Success",
          description: "Task deleted successfully",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: "Failed to delete task",
          variant: "destructive",
        });
      },
    }
  );
};

// Hook for deleting materials
export const useDeleteSopMaterial = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: string; materialName: string }) => {
      return serviceProcedureApi.deleteSopMaterial({
        sopId: parseInt(params.sopId),
        materialName: params.materialName,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("material"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });

        toast({
          title: "Success",
          description: "Material deleted successfully",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: "Failed to delete material",
          variant: "destructive",
        });
      },
    }
  );
};

// Hook for deleting readings
export const useDeleteSopReading = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: string; readingName: string }) => {
      return serviceProcedureApi.deleteSopReading({
        sopId: parseInt(params.sopId),
        readingName: params.readingName,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the specific tab data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopTabDetails("readings"),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });

        toast({
          title: "Success",
          description: "Reading deleted successfully",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: "Failed to delete reading",
          variant: "destructive",
        });
      },
    }
  );
};
export const useDeleteSopNote = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (params: { sopId: string; noteCode: string }) => {
      return serviceProcedureApi.deleteSopNote({
        sopId: parseInt(params.sopId),
        noteCode: params.noteCode,
      });
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate the notes data
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopNotes(variables.sopId),
          exact: false,
        });

        // Also invalidate the main SOP details
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.master.sopMasterDetails(
            variables.sopId
          ),
          exact: false,
        });

        toast({
          title: "Success",
          description: "Note deleted successfully",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: "Failed to delete note",
          variant: "destructive",
        });
      },
    }
  );
};
